import React, { useMemo, useState } from 'react';
import { Database, Download } from 'lucide-react';
import { AssertionList, type EvidenceAssertionRow } from './AssertionList';
import { AssertionDetailDrawer } from './AssertionDetailDrawer';
import { LineageDetailDrawer, type EvidenceLineageRow } from './LineageDetailDrawer';

export function EvidencePanel(props: {
  runId: string;
  evidence: {
    assertions: EvidenceAssertionRow[];
    lineage: EvidenceLineageRow[];
    warnings?: string[];
  };
  conflicts?: Array<{ metricKey: string; values: Array<number | string | null>; assertionIds: string[] }>;
  selectedMetricKey?: string | null;
  onSelectMetricKey?: (mk: string | null) => void;
}): JSX.Element {
  const { runId, evidence, conflicts, selectedMetricKey, onSelectMetricKey } = props;
  const [selectedAssertion, setSelectedAssertion] = useState<EvidenceAssertionRow | null>(null);
  const [lineageRefId, setLineageRefId] = useState<string | null>(null);

  const assertions = useMemo(() => {
    if (!selectedMetricKey) return evidence.assertions || [];
    return (evidence.assertions || []).filter(a => a.metricKey === selectedMetricKey);
  }, [evidence.assertions, selectedMetricKey]);

  const onExport = () => {
    const payload = {
      runId,
      exportedAt: new Date().toISOString(),
      evidence
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence-${runId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-blue-600" />
          <div className="font-semibold text-gray-900">Evidence</div>
          <div className="text-xs text-gray-500">
            {evidence.assertions?.length || 0} assertions • {evidence.lineage?.length || 0} tool calls
          </div>
        </div>
        <button
          onClick={onExport}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
        >
          <Download className="w-4 h-4" />
          Export JSON
        </button>
      </div>

      {conflicts?.length ? (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3">
          <div className="text-sm font-semibold text-red-800">Conflicts</div>
          <div className="mt-1 space-y-1">
            {conflicts.slice(0, 6).map((c) => (
              <button
                key={c.metricKey}
                onClick={() => onSelectMetricKey?.(c.metricKey)}
                className="w-full text-left text-xs text-red-800 hover:underline font-mono"
              >
                {c.metricKey}
              </button>
            ))}
          </div>
          {selectedMetricKey ? (
            <button onClick={() => onSelectMetricKey?.(null)} className="mt-2 text-xs text-red-700 hover:underline">
              Clear filter
            </button>
          ) : null}
        </div>
      ) : null}

      {evidence.warnings?.length ? (
        <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">
          {evidence.warnings.slice(0, 4).map((w, i) => (
            <div key={i}>- {w}</div>
          ))}
        </div>
      ) : null}

      <AssertionList
        assertions={assertions}
        selectedMetricKey={selectedMetricKey}
        onSelectAssertion={(a) => setSelectedAssertion(a)}
      />

      <AssertionDetailDrawer
        isOpen={!!selectedAssertion}
        assertion={selectedAssertion}
        onClose={() => setSelectedAssertion(null)}
        onSelectLineageRef={(ref) => setLineageRefId(ref)}
      />

      <LineageDetailDrawer
        isOpen={!!lineageRefId}
        runId={runId}
        lineageRefId={lineageRefId}
        onClose={() => setLineageRefId(null)}
        localLineage={evidence.lineage}
      />
    </div>
  );
}


